import React, { useState, useEffect, useRef } from "react";
import { useApp } from "../context/AppContext";
import Sidebar from "../components/Sidebar";
import { getCompany, getContacts, patchCompany, patchContacts, getFeedback, upsertFeedback } from "../services/api";
import { marketingBucketOf } from "../config/marketingBuckets";
import { CONTACT_SOURCES, contactOrigin, formatPhoneValue, COMPETITOR_CONTACT_SOURCE } from "../config/contactSources";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { ExternalLink, ChevronUp, ChevronDown, RefreshCw, Pencil, Sparkles, Star, ThumbsUp, ThumbsDown, Info } from "lucide-react";

const F = "'Montserrat',sans-serif";
const M = "'Montserrat',sans-serif";

// ── Helpers ───────────────────────────────────────────────────────────────────

const FIT_COLOR = { high: "#16a34a", High: "#16a34a", medium: "#C9760A", Medium: "#C9760A", low: "#C1121F", Low: "#C1121F" };
const FIT_BG    = { high: "#dcfce7", High: "#dcfce7", medium: "#fef4e6", Medium: "#fef4e6", low: "#fdecec", Low: "#fdecec" };

const COUNTRY_FLAGS = {
  "United Arab Emirates":"🇦🇪","UAE":"🇦🇪","China":"🇨🇳","India":"🇮🇳",
  "Germany":"🇩🇪","France":"🇫🇷","United States":"🇺🇸","USA":"🇺🇸",
  "Japan":"🇯🇵","Italy":"🇮🇹","Spain":"🇪🇸","Turkey":"🇹🇷","Türkiye":"🇹🇷","Brazil":"🇧🇷",
  "Netherlands":"🇳🇱","United Kingdom":"🇬🇧","UK":"🇬🇧","Canada":"🇨🇦",
  "Australia":"🇦🇺","South Korea":"🇰🇷","Mexico":"🇲🇽","Russia":"🇷🇺",
  "Poland":"🇵🇱","Belgium":"🇧🇪","Sweden":"🇸🇪","Switzerland":"🇨🇭",
  "Austria":"🇦🇹","Argentina":"🇦🇷","Indonesia":"🇮🇩","Thailand":"🇹🇭",
  "Vietnam":"🇻🇳","Malaysia":"🇲🇾","Singapore":"🇸🇬","Egypt":"🇪🇬",
  "Morocco":"🇲🇦","South Africa":"🇿🇦","Greece":"🇬🇷","Portugal":"🇵🇹",
  "Denmark":"🇩🇰","Finland":"🇫🇮","Norway":"🇳🇴","Israel":"🇮🇱",
  "Saudi Arabia":"🇸🇦","Iran":"🇮🇷","Pakistan":"🇵🇰","Philippines":"🇵🇭",
  "Nigeria":"🇳🇬","Chile":"🇨🇱","Colombia":"🇨🇴","Romania":"🇷🇴",
  "New Zealand":"🇳🇿","Taiwan":"🇹🇼","Hong Kong":"🇭🇰","Ukraine":"🇺🇦",
  "Azerbaijan":"🇦🇿","Georgia":"🇬🇪","Armenia":"🇦🇲","Kazakhstan":"🇰🇿",
  "Uzbekistan":"🇺🇿","Qatar":"🇶🇦","Kuwait":"🇰🇼","Jordan":"🇯🇴",
  "Iraq":"🇮🇶","Oman":"🇴🇲","Bahrain":"🇧🇭","Lebanon":"🇱🇧",
  "Algeria":"🇩🇿","Tunisia":"🇹🇳","Libya":"🇱🇾","Ethiopia":"🇪🇹",
  "Kenya":"🇰🇪","Ghana":"🇬🇭","Czech Republic":"🇨🇿","Hungary":"🇭🇺",
};

function getFlag(country) {
  if (!country) return "🏳";
  const key = Object.keys(COUNTRY_FLAGS).find(k =>
    country.toLowerCase().includes(k.toLowerCase()));
  return key ? COUNTRY_FLAGS[key] : "🏳";
}

// Handle both comma- and pipe-separated strings
function splitCSV(s) {
  if (!s) return [];
  const sep = s.includes("|") ? "|" : s.includes(";") ? ";" : ",";
  return s.split(sep).map(x => x.trim()).filter(Boolean);
}

function capitalize(s) {
  if (s == null || s === "") return s;
  const str = String(s);
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

// ── Micro-components ──────────────────────────────────────────────────────────

function FitBadge({ value }) {
  const v  = value != null ? String(value) : "";
  const c  = FIT_COLOR[v] || "#818084";
  const bg = FIT_BG[v]   || "#F4F4F5";
  return (
    <span style={{ display:"inline-block", padding:"4px 14px", borderRadius:20,
      fontSize:11, fontWeight:700, color:c, background:bg, letterSpacing:0.2 }}>
      {capitalize(v) || "—"}
    </span>
  );
}

// Colored pill tags, styled by category
function Tag({ label, color, bg }) {
  return (
    <span style={{
      display:"inline-block", padding:"5px 14px", borderRadius:6,
      fontSize:11, fontWeight:500, color, background:bg,
      border:`1px solid ${color}25`, margin:"3px 5px 3px 0",
      letterSpacing:0.1, lineHeight:1.3,
    }}>
      {label}
    </span>
  );
}

// Grey fallback tag (for unknown lists)
function GrayTag({ label }) {
  return <Tag label={label} color="#3F3F46" bg="#F4F4F5" />;
}

// The competitor show website this company was scraped from (captured on the
// Add Show form). Sources tagged "competitor website" cite this URL.
const ShowWebsiteContext = React.createContext(null);

const toHref = (u) => (u && u.startsWith("www.") ? "https://" + u : u);

// ── Source type label ─────────────────────────────────────────────────────────
// CiteLink renders a small icon (↗) next to any enriched field.
// Clicking the icon shows a compact popover naming the source:
//   Website — extracted from the company's own website via Perplexity
//   LinkedIn — extracted from the LinkedIn company page
//   Competitor — pre-populated from the competitor show website

function _sourceLabel(source) {
  if (!source) return null;
  const lower = source.toLowerCase().trim();
  if (lower === COMPETITOR_CONTACT_SOURCE.toLowerCase()) return "Competitor";
  if (lower.startsWith("competitor website")) return "Competitor";
  if (lower === "linkedin") return "LinkedIn";
  const parts = lower.split("|").map(s => s.trim());
  if (parts.some(p => p.includes("linkedin.com"))) return "LinkedIn";
  if (lower === "website" || parts.some(p => p.startsWith("http") || p.startsWith("www."))) return "Website";
  return null;
}


function _domain(url) {
  try { return new URL(url).hostname.replace(/^www\./, ""); }
  catch { return url; }
}

function _sourceUrls(source, showWebsite) {
  if (!source) return [];
  const lower = source.toLowerCase().trim();
  if ((lower.startsWith("competitor website") || lower === COMPETITOR_CONTACT_SOURCE.toLowerCase()) && showWebsite) {
    return [toHref(showWebsite)];
  }
  const urls = source.split("|")
    .map(s => s.trim())
    .filter(s => s.startsWith("http") || s.startsWith("www."))
    .map(toHref);
  // Deduplicate by domain — one clickable entry per unique hostname
  const seen = new Set();
  return urls.filter(url => {
    const d = _domain(url);
    if (seen.has(d)) return false;
    seen.add(d);
    return true;
  });
}

function CiteLink({ source }) {
  const [open, setOpen] = useState(false);
  const showWebsite = React.useContext(ShowWebsiteContext);
  const label = _sourceLabel(source);
  if (!label) return null;
  const urls = _sourceUrls(source, showWebsite);
  return (
    <div style={{ position:"relative", display:"inline-block", flexShrink:0 }}>
      <button
        onClick={() => setOpen(o => !o)}
        title={label}
        style={{
          background:"none", border:"1px solid #D8DADC", borderRadius:3,
          padding:"2px 4px", cursor:"pointer", color:"#AFABAB",
          display:"inline-flex", alignItems:"center", lineHeight:1,
        }}
      >
        <ExternalLink size={11}/>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position:"fixed", inset:0, zIndex:199 }} />
          <div style={{
            position:"absolute", bottom:"calc(100% + 4px)", left:0, zIndex:200,
            background:"#fff", border:"1px solid #E4E4E7", borderRadius:6,
            boxShadow:"0 4px 14px rgba(0,0,0,0.10)", padding:"8px 12px",
            minWidth:160, maxWidth:360,
          }}>
            <div style={{ fontSize:9, fontWeight:700, color:"#818084",
              textTransform:"uppercase", letterSpacing:0.5,
              marginBottom: urls.length > 0 ? 6 : 0 }}>
              {label}
            </div>
            {urls.map((url, i) => (
              <a key={i} href={url} target="_blank" rel="noreferrer"
                 onClick={() => setOpen(false)}
                 style={{
                   display:"block", fontSize:10, color:"#818084",
                   textDecoration:"none", wordBreak:"break-all",
                   paddingTop: i > 0 ? 4 : 0, lineHeight:1.4,
                 }}
                 onMouseEnter={e => e.currentTarget.style.color = "#3F3F46"}
                 onMouseLeave={e => e.currentTarget.style.color = "#818084"}>
                {_domain(url)}
              </a>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const EMPTY_STRS = new Set(["", "—", "unknown", "not found", "none found", "not available", "n/a", "na"]);

const STATUS_STYLE = {
  Friendly:   { color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
  Unfriendly: { color: "#A00F1A", bg: "#fdecec", border: "#fbd5d5" },
  Unknown:    { color: "#818084", bg: "#FAFAFA", border: "#D8DADC" },
};
// Shown on the Website row when the company had no website to enrich from.
//
// It matters because it changes what the rest of the profile rests on. With a
// website, extraction is restricted to that domain and the company's identity is
// certain. Without one, the search is open and only the company name, country and
// any supplied description keep it on the right company — so every other field
// here is a degree less certain, and nothing else on the page says so.
function NoWebsiteTag() {
  return (
    <span title="No website was supplied for this company, so it was enriched from an open web search and its LinkedIn page."
      style={{
        display:"inline-block", fontSize:9, fontWeight:600, padding:"2px 8px",
        borderRadius:20, color:"#9a5b00", background:"#fef4e6", border:"1px solid #fde3bd",
        letterSpacing:0.1, whiteSpace:"nowrap",
      }}>
      No website
    </span>
  );
}

function CountryStatusTag({ status }) {
  if (!status) return null;
  const s = STATUS_STYLE[status] || STATUS_STYLE.Unknown;
  return (
    <span style={{
      display:"inline-block", fontSize:9, fontWeight:600, padding:"2px 8px",
      borderRadius:20, color:s.color, background:s.bg, border:`1px solid ${s.border}`,
      letterSpacing:0.1, whiteSpace:"nowrap",
    }}>
      {status}
    </span>
  );
}


function InfoRow({ label, value, href, mono, source, badge, note, hint, origin }) {
  const val = value != null && !EMPTY_STRS.has(String(value).toLowerCase().trim()) ? value : null;
  const hasNote = note && !EMPTY_STRS.has(String(note).toLowerCase().trim());
  return (
    <div style={{ display:"flex", gap:16, padding:"9px 0",
      borderBottom:"1px solid #F4F4F5", alignItems:"flex-start" }}>
      <div style={{ width:150, flexShrink:0, fontSize:11, color:"#818084", fontWeight:500 }}>
        {label}
      </div>
      <div style={{ flex:1, fontSize:11, color:"#3F3F46",
        fontFamily:mono ? M : F, wordBreak:"break-word" }}>
        <div style={{ display:"flex", alignItems:"center", gap:7, flexWrap:"wrap" }}>
          {val && origin && <SourceDot origin={origin} size={6} />}
          <span>
            {href && val
              ? <a href={href} target="_blank" rel="noreferrer"
                   style={{ color:"#E8431A", textDecoration:"none" }}>{val} <ExternalLink size={11}/></a>
              : val || <span style={{ color:"#D8DADC" }}>Not available</span>}
          </span>
          {badge}
          {val && <CiteLink source={source} />}
        </div>
        {!val && hint && (
          <div style={{ fontSize:10, color:"#D8DADC", marginTop:3 }}>
            {hint}
          </div>
        )}
        {hasNote && (
          <div style={{ fontSize:10, color:"#818084", marginTop:4, lineHeight:1.5,
            fontStyle:"italic" }}>
            {note}
          </div>
        )}
      </div>
    </div>
  );
}

// Small provenance dot shown next to a contact (origin → colour from CONTACT_SOURCES).
function SourceDot({ origin, size = 8 }) {
  const cfg = origin && CONTACT_SOURCES[origin];
  if (!cfg) return null;
  return (
    <span title={cfg.label} style={{
      width:size, height:size, borderRadius:"50%", background:cfg.color,
      display:"inline-block", flexShrink:0 }} />
  );
}

// One-time key for the Contacts section — minimal, lists only origins present.
function ContactLegend({ origins }) {
  if (!origins.length) return null;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:14, flexWrap:"wrap",
      marginBottom:10, fontSize:9.5, color:"#818084" }}>
      {origins.map(o => (
        <span key={o} style={{ display:"inline-flex", alignItems:"center", gap:5 }}>
          <SourceDot origin={o} size={6} /> {CONTACT_SOURCES[o].label}
        </span>
      ))}
    </div>
  );
}

// ── Edit-mode input rows ──────────────────────────────────────────────────────

const inputStyle = {
  flex:1, fontSize:11, border:"1px solid #F7C9B8", borderRadius:6,
  padding:"6px 10px", outline:"none", color:"#26262A",
  background:"#f0f7ff", fontFamily:"inherit",
};

function ERow({ label, field, draft, onChange, mono, source, placeholder }) {
  return (
    <div style={{ display:"flex", gap:16, padding:"7px 0",
      borderBottom:"1px solid #F4F4F5", alignItems:"center" }}>
      <div style={{ width:150, flexShrink:0, fontSize:11, color:"#818084", fontWeight:500 }}>
        {label}
      </div>
      <input
        value={draft[field] ?? ""}
        onChange={e => onChange(field, e.target.value)}
        placeholder={placeholder || ""}
        style={{ ...inputStyle, fontFamily: mono ? "'Montserrat',sans-serif" : "inherit" }}
      />
      <CiteLink source={source} />
    </div>
  );
}

function ESelectRow({ label, field, draft, onChange, options }) {
  return (
    <div style={{ display:"flex", gap:16, padding:"7px 0",
      borderBottom:"1px solid #F4F4F5", alignItems:"center" }}>
      <div style={{ width:150, flexShrink:0, fontSize:11, color:"#818084", fontWeight:500 }}>
        {label}
      </div>
      <select
        value={draft[field] ?? ""}
        onChange={e => onChange(field, e.target.value)}
        style={{ ...inputStyle, cursor:"pointer" }}>
        <option value="">— Not set —</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function ETagRow({ label, field, draft, onChange, palette, source }) {
  const { color, bg } = TAG_PALETTES[palette || "default"];
  const preview = (draft[field] || "").split("|").map(s => s.trim()).filter(Boolean);
  return (
    <div style={{ marginBottom:22 }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
        <div style={{ fontSize:10, fontWeight:700, color:"#818084",
          textTransform:"uppercase", letterSpacing:0.6 }}>{label}</div>
        <CiteLink source={source} />
      </div>
      <input
        value={draft[field] ?? ""}
        onChange={e => onChange(field, e.target.value)}
        placeholder="Pipe-separated values (e.g. Value 1 | Value 2)"
        style={{ ...inputStyle, width:"100%", boxSizing:"border-box" }}
      />
      {preview.length > 0 && (
        <div style={{ display:"flex", flexWrap:"wrap", marginTop:8 }}>
          {preview.map((p, i) => <Tag key={i} label={p} color={color} bg={bg} />)}
        </div>
      )}
    </div>
  );
}

function SectionCard({ title, id, open, onToggle, alwaysOpen, children }) {
  return (
    <section id={id} style={{ background:"#fff", borderRadius:12,
      border:"1px solid #D8DADC", marginBottom:16, overflow:"hidden" }}>
      <button onClick={alwaysOpen ? undefined : onToggle} style={{
        width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between",
        padding:"14px 28px", background:"none", border:"none",
        cursor: alwaysOpen ? "default" : "pointer",
        borderBottom: open ? "1px solid #D8DADC" : "none", textAlign:"left",
      }}>
        <h3 style={{ fontSize:11, fontWeight:700, color:"#818084",
          margin:0, letterSpacing:0.8, textTransform:"uppercase" }}>
          {title}
        </h3>
        {!alwaysOpen && (open ? <ChevronUp size={14} color="#818084"/> : <ChevronDown size={14} color="#818084"/>)}
      </button>
      {open && <div style={{ padding:"24px 28px" }}>{children}</div>}
    </section>
  );
}

function SubLabel({ children }) {
  return (
    <div style={{ fontSize:10, fontWeight:700, color:"#818084",
      textTransform:"uppercase", letterSpacing:0.6, marginBottom:10 }}>
      {children}
    </div>
  );
}


// ── Tag group with colored pills per category ─────────────────────────────────

const TAG_PALETTES = {
  products:    { color:"#E8431A", bg:"#FCEBE5" },
  customers:   { color:"#16a34a", bg:"#dcfce7" },
  markets:     { color:"#5F1156", bg:"#EFDDE8" },
  certs:       { color:"#C23417", bg:"#f9d9a8" },
  default:     { color:"#3F3F46", bg:"#F4F4F5" },
};

function TagGroup({ label, items, palette = "default", source }) {
  const { color, bg } = TAG_PALETTES[palette];
  return (
    <div style={{ marginBottom:22 }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
        <div style={{ fontSize:10, fontWeight:700, color:"#818084",
          textTransform:"uppercase", letterSpacing:0.6 }}>
          {label}
        </div>
        <CiteLink source={source} />
      </div>
      <div style={{ display:"flex", flexWrap:"wrap" }}>
        {(() => {
          const valid = items.filter(p => !EMPTY_STRS.has(String(p).toLowerCase().trim()));
          return valid.length > 0
            ? valid.map((p, i) => <Tag key={i} label={p} color={color} bg={bg} />)
            : <span style={{ fontSize:11, color:"#D8DADC" }}>Not available</span>;
        })()}
      </div>
    </div>
  );
}

// ── World Map ─────────────────────────────────────────────────────────────────

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const GEO_LOOKUP = {
  // Europe — countries
  "russia":[61.5,105.3],"germany":[51.2,10.4],"france":[46.2,2.2],
  "united kingdom":[55.4,-3.4],"uk":[55.4,-3.4],"england":[52.0,-0.5],
  "italy":[41.9,12.6],"spain":[40.5,-3.7],"netherlands":[52.1,5.3],
  "belgium":[50.5,4.5],"poland":[51.9,19.1],"turkey":[38.9,35.2],"türkiye":[38.9,35.2],
  "sweden":[60.1,18.6],"norway":[60.5,8.5],"denmark":[56.3,9.5],
  "finland":[64.9,26.0],"switzerland":[46.8,8.2],"austria":[47.5,14.5],
  "portugal":[39.4,-8.2],"greece":[39.1,21.8],"ukraine":[48.4,31.2],
  "romania":[45.9,24.9],"czech republic":[49.8,15.5],"czechia":[49.8,15.5],
  "hungary":[47.2,19.5],"slovakia":[48.7,19.7],"ireland":[53.4,-8.2],
  "croatia":[45.1,15.2],"serbia":[44.0,21.0],"bulgaria":[42.7,25.5],
  // Middle East
  "united arab emirates":[24.5,54.5],"uae":[24.5,54.5],
  "saudi arabia":[23.9,45.1],"israel":[31.0,34.9],"iran":[32.4,53.7],
  "qatar":[25.4,51.2],"kuwait":[29.3,47.5],"jordan":[30.6,36.5],
  "oman":[21.5,55.9],"bahrain":[26.0,50.6],"iraq":[33.2,43.7],
  // Asia
  "china":[35.8,104.1],"india":[20.6,78.9],"japan":[36.2,138.3],
  "south korea":[35.9,127.8],"korea":[35.9,127.8],"thailand":[15.9,100.9],
  "vietnam":[14.1,108.3],"malaysia":[4.2,101.9],"singapore":[1.4,103.8],
  "indonesia":[-0.8,113.9],"philippines":[12.9,121.8],"taiwan":[23.7,121.0],
  "hong kong":[22.3,114.2],"pakistan":[30.4,69.3],"kazakhstan":[48.0,66.9],
  "myanmar":[16.9,96.1],"cambodia":[11.6,104.9],"sri lanka":[7.9,80.7],
  "bangladesh":[23.7,90.4],"uzbekistan":[41.4,64.6],"kyrgyzstan":[41.2,74.8],
  // Caucasus
  "azerbaijan":[40.1,47.6],"georgia":[42.3,43.4],"armenia":[40.1,45.0],
  // Middle East (extras)
  "lebanon":[33.9,35.9],"syria":[34.8,38.9],"yemen":[15.6,48.5],
  "palestine":[31.9,35.2],"libya":[26.3,17.2],
  // Americas
  "united states":[37.1,-95.7],"usa":[37.1,-95.7],"us":[37.1,-95.7],
  "canada":[56.1,-106.3],"brazil":[-14.2,-51.9],"argentina":[-38.4,-63.6],
  "mexico":[23.6,-102.5],"chile":[-35.7,-71.5],"colombia":[4.6,-74.1],
  "peru":[-9.2,-75.0],"ecuador":[-1.8,-78.2],"venezuela":[6.4,-66.6],
  // Africa
  "south africa":[-28.5,24.7],"egypt":[26.8,30.8],"morocco":[31.8,-7.1],
  "nigeria":[9.1,8.7],"ethiopia":[9.1,40.5],"kenya":[-0.0,37.9],
  "algeria":[28.0,1.7],"tunisia":[33.9,9.5],"ghana":[8.0,-2.0],
  // Oceania
  "australia":[-25.3,133.8],"new zealand":[-40.9,174.9],
  // Cities — Middle East
  "dubai":[25.2,55.3],"abu dhabi":[24.5,54.4],"riyadh":[24.7,46.7],
  "doha":[25.3,51.5],"muscat":[23.6,58.6],"jeddah":[21.5,39.2],
  // Cities — Europe
  "paris":[48.85,2.35],"cologne":[50.9,6.9],"london":[51.5,-0.1],
  "berlin":[52.5,13.4],"frankfurt":[50.1,8.7],"munich":[48.1,11.6],
  "hamburg":[53.6,10.0],"milan":[45.5,9.2],"rome":[41.9,12.5],
  "madrid":[40.4,-3.7],"barcelona":[41.4,2.2],"amsterdam":[52.4,4.9],
  "brussels":[50.8,4.4],"vienna":[48.2,16.4],"zurich":[47.4,8.5],
  "stockholm":[59.3,18.1],"oslo":[59.9,10.7],"copenhagen":[55.7,12.6],
  "helsinki":[60.2,24.9],"warsaw":[52.2,21.0],"prague":[50.1,14.4],
  "budapest":[47.5,19.0],"istanbul":[41.0,29.0],"athens":[37.9,23.7],
  "moscow":[55.8,37.6],"st. petersburg":[59.9,30.3],
  "vigo":[42.2,-8.7],"lyon":[45.7,4.8],"toulouse":[43.6,1.4],
  // Cities — Asia
  "tokyo":[35.7,139.7],"guangzhou":[23.1,113.3],
  "beijing":[39.9,116.4],"shanghai":[31.2,121.5],
  "seoul":[37.6,127.0],"taipei":[25.1,121.6],"mumbai":[19.1,72.9],
  "delhi":[28.7,77.2],"bangalore":[12.9,77.6],"bangkok":[13.8,100.5],
  "kuala lumpur":[3.1,101.7],"jakarta":[-6.2,106.8],"manila":[14.6,121.0],
  "osaka":[34.7,135.5],"nagoya":[35.2,137.0],"ho chi minh city":[10.8,106.7],
  // Cities — Americas
  "new york":[40.7,-74.0],"los angeles":[34.1,-118.2],"chicago":[41.9,-87.6],
  "toronto":[43.7,-79.4],"vancouver":[49.3,-123.1],"mexico city":[19.4,-99.1],
  "são paulo":[-23.5,-46.6],"sao paulo":[-23.5,-46.6],"rio de janeiro":[-22.9,-43.2],
  "buenos aires":[-34.6,-58.4],"lima":[-12.1,-77.0],"bogota":[4.7,-74.1],
  "santiago":[-33.5,-70.7],
  // Cities — Africa / Oceania
  "cairo":[30.1,31.2],"johannesburg":[-26.2,28.0],"cape town":[-33.9,18.4],
  "nairobi":[-1.3,36.8],"lagos":[6.5,3.4],"casablanca":[33.6,-7.6],
  "sydney":[-33.9,151.2],"melbourne":[-37.8,145.0],"auckland":[-36.9,174.8],
  // Trade show names → venue city
  "gulfood":[25.2,55.3],"anuga":[50.9,6.9],"sial":[48.85,2.35],
  "foodex":[35.7,139.7],"fha":[1.35,103.8],"anufood":[-23.5,-46.6],
  "canton fair":[23.1,113.3],"conxemar":[42.2,-8.7],
  "biofach":[49.5,11.1],"fancy food":[40.7,-74.0],"thaifex":[13.8,100.5],
  // Regions / blocs
  "europe":[50.0,10.0],"middle east":[25.0,45.0],"asia":[20.0,90.0],
  "southeast asia":[5.0,110.0],"north america":[40.0,-95.0],
  "south america":[-15.0,-60.0],"africa":[5.0,20.0],
  "gulf":[25.0,50.0],"mena":[25.0,35.0],"apac":[-10.0,140.0],
  "gcc":[25.0,50.0],"asean":[5.0,110.0],
};

function lookupCoords(name) {
  if (!name) return null;
  const key = name.toLowerCase().trim();
  if (GEO_LOOKUP[key]) {
    const [lat, lng] = GEO_LOOKUP[key];
    return { lat, lng, label: name };
  }
  const match = Object.keys(GEO_LOOKUP).find(k =>
    key.includes(k) || (k.length > 4 && k.includes(key))
  );
  if (match) {
    const [lat, lng] = GEO_LOOKUP[match];
    return { lat, lng, label: name };
  }
  return null;
}

function WorldMap({ hqCountry, hqCity, eventLocations, marketLocations = [] }) {
  const [tooltip, setTooltip] = useState(null);

  const hqPt = lookupCoords(hqCity) || lookupCoords(hqCountry);

  const seen = new Set();
  const evtPts = eventLocations
    .map(loc => lookupCoords(loc))
    .filter(Boolean)
    .filter(pt => {
      const k = `${Math.round(pt.lat)},${Math.round(pt.lng)}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });

  const mktSeen = new Set();
  const mktPts = marketLocations
    .map(loc => lookupCoords(loc))
    .filter(Boolean)
    .filter(pt => {
      const k = `${Math.round(pt.lat)},${Math.round(pt.lng)}`;
      if (mktSeen.has(k)) return false;
      mktSeen.add(k);
      return true;
    });

  if (!hqPt && evtPts.length === 0) return null;

  return (
    <div style={{ position:"relative", borderRadius:10,
      border:"1px solid #c8d8f0", overflow:"hidden",
      background:"#ddeaf8" }}>
      <ComposableMap
        projectionConfig={{ scale: 147, center: [10, 10] }}
        style={{ width:"100%", height:"auto" }}>

        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies.map(geo => (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                fill="#b8cfe8"
                stroke="#a8c0dc"
                strokeWidth={0.5}
                style={{
                  default: { outline:"none" },
                  hover:   { fill:"#b8cfe8", outline:"none" },
                  pressed: { outline:"none" },
                }}
              />
            ))
          }
        </Geographies>

        {/* Target market pins */}
        {mktPts.map((pt, i) => (
          <Marker key={`mkt-${i}`} coordinates={[pt.lng, pt.lat]}
            onMouseEnter={() => setTooltip({ label: pt.label, type:"mkt" })}
            onMouseLeave={() => setTooltip(null)}>
            <circle r={10} fill="#5F1156" opacity={0.15}/>
            <circle r={5} fill="#5F1156" stroke="#fff" strokeWidth={1.5}
              style={{ cursor:"default" }}/>
          </Marker>
        ))}

        {/* Event footprint pins */}
        {evtPts.map((pt, i) => (
          <Marker key={`evt-${i}`} coordinates={[pt.lng, pt.lat]}
            onMouseEnter={() => setTooltip({ label: pt.label, type:"evt" })}
            onMouseLeave={() => setTooltip(null)}>
            <circle r={5} fill="#E8431A" stroke="#fff" strokeWidth={1.5}
              style={{ cursor:"default" }}/>
            <circle r={10} fill="#E8431A" opacity={0.15}/>
          </Marker>
        ))}

        {/* HQ pin */}
        {hqPt && (
          <Marker coordinates={[hqPt.lng, hqPt.lat]}
            onMouseEnter={() => setTooltip({ label: hqCity || hqCountry, type:"hq" })}
            onMouseLeave={() => setTooltip(null)}>
            <circle r={10} fill="#E8431A" opacity={0.2}/>
            <circle r={7} fill="#E8431A" stroke="#fff" strokeWidth={2}
              style={{ cursor:"default" }}/>
            <circle r={3} fill="#fff"/>
          </Marker>
        )}
      </ComposableMap>

      {/* Hover tooltip */}
      {tooltip && (
        <div style={{
          position:"absolute", top:8, left:12,
          background: tooltip.type === "hq" ? "#E8431A" : tooltip.type === "mkt" ? "#5F1156" : "#166534",
          color:"#fff", padding:"3px 10px", borderRadius:5,
          fontSize:10, fontWeight:600, pointerEvents:"none",
          boxShadow:"0 2px 6px rgba(0,0,0,0.18)",
        }}>
          {tooltip.type === "hq" ? `HQ · ${tooltip.label}` : tooltip.label}
        </div>
      )}

      {/* Legend */}
      <div style={{
        position:"absolute", bottom:6, right:8,
        background:"rgba(255,255,255,0.92)", borderRadius:5,
        padding:"3px 9px", display:"flex", gap:10, alignItems:"center",
        fontSize:9, color:"#3F3F46",
      }}>
        <span style={{ display:"flex", gap:4, alignItems:"center" }}>
          <span style={{ width:8,height:8,borderRadius:"50%",background:"#E8431A",display:"inline-block" }}/>
          HQ
        </span>
        {mktPts.length > 0 && (
          <span style={{ display:"flex", gap:4, alignItems:"center" }}>
            <span style={{ width:8,height:8,borderRadius:"50%",background:"#5F1156",display:"inline-block" }}/>
            Markets ({mktPts.length})
          </span>
        )}
        {evtPts.length > 0 && (
          <span style={{ display:"flex", gap:4, alignItems:"center" }}>
            <span style={{ width:8,height:8,borderRadius:"50%",background:"#16a34a",display:"inline-block" }}/>
            Events ({evtPts.length})
          </span>
        )}
      </div>
    </div>
  );
}

// ── Citation log (collapsible) ────────────────────────────────────────────────

function CitationRow({ label, value }) {
  if (!value) return null;
  const isUrl = value.startsWith("http") || value.startsWith("www.");
  const url   = isUrl && value.startsWith("www.") ? "https://" + value : value;
  return (
    <div style={{ display:"flex", gap:14, padding:"6px 0",
      borderBottom:"1px solid #FAFAFA", alignItems:"flex-start" }}>
      <div style={{ width:180, flexShrink:0, fontSize:10, color:"#818084" }}>{label}</div>
      <div style={{ flex:1, fontSize:10, wordBreak:"break-all" }}>
        {isUrl
          ? <a href={url} target="_blank" rel="noreferrer"
               style={{ color:"#E8431A", textDecoration:"none" }}>{value} <ExternalLink size={11}/></a>
          : <span style={{ color:"#818084" }}>{value}</span>}
      </div>
    </div>
  );
}

// Fields whose values are Perplexity citation URL lists (pipe-separated).
// Their URLs are collected into one flat deduplicated "Raw Sources" list.
const _RAW_SOURCE_FIELDS = [
  "hq_country_source", "hq_city_source", "physical_address_source",
  "email_source", "phone_source", "social_media_source",
  "core_products_source", "target_customers_source", "target_market_source",
  "company_type_citation", "hq_type_citation",
  "certifications_source", "distributor_presence_source", "product_category_source",
];

// Event sources are kept as labeled rows — each entry is a specific event post URL.
const _EVENT_SOURCE_ROWS = [
  ["Shows Attended", "past_events_source"],
  ["Active Regions", "event_regions_source"],
];

function _extractUrls(source) {
  if (!source) return [];
  return source.split("|").map(s => s.trim()).filter(s => s.startsWith("http") || s.startsWith("www."));
}

function CitationLog({ co }) {
  const [open, setOpen] = useState(false);

  const rawUrls = [...new Set(_RAW_SOURCE_FIELDS.flatMap(f => _extractUrls(co[f])))];
  const eventRows = _EVENT_SOURCE_ROWS.map(([label, field]) => [label, co[field]]).filter(([, v]) => v);

  const totalCount = rawUrls.length + eventRows.length;
  if (totalCount === 0) return null;

  return (
    <div style={{ marginBottom:16 }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ display:"flex", alignItems:"center", gap:8, width:"100%",
          background:"#fff", border:"1px solid #D8DADC", borderRadius:open ? "10px 10px 0 0" : 10,
          padding:"11px 18px", cursor:"pointer", fontFamily:F }}>
        <span style={{ fontSize:10, fontWeight:700, color:"#818084",
          textTransform:"uppercase", letterSpacing:0.7, flex:1, textAlign:"left" }}>
          AI Source Citations
        </span>
        <span style={{ fontSize:10, color:"#818084",
          background:"#F4F4F5", borderRadius:10, padding:"2px 9px", fontWeight:600 }}>
          {totalCount}
        </span>
        <span style={{ fontSize:11, color:"#818084" }}>{open ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}</span>
      </button>
      {open && (
        <div style={{ background:"#fff", border:"1px solid #D8DADC", borderTop:"none",
          borderRadius:"0 0 10px 10px", padding:"12px 18px" }}>

          {rawUrls.length > 0 && (
            <>
              <div style={{ fontSize:9, fontWeight:700, color:"#818084",
                textTransform:"uppercase", letterSpacing:0.6, marginBottom:6 }}>
                Raw Sources
              </div>
              {rawUrls.map((url, i) => (
                <div key={i} style={{ padding:"4px 0", borderBottom:"1px solid #FAFAFA" }}>
                  <a href={url} target="_blank" rel="noreferrer"
                     style={{ fontSize:10, color:"#E8431A", textDecoration:"none", wordBreak:"break-all" }}>
                    {url} <ExternalLink size={10}/>
                  </a>
                </div>
              ))}
            </>
          )}

          {eventRows.length > 0 && (
            <>
              <div style={{ fontSize:9, fontWeight:700, color:"#818084",
                textTransform:"uppercase", letterSpacing:0.6,
                marginTop:rawUrls.length > 0 ? 14 : 0, marginBottom:6 }}>
                Event Sources
              </div>
              {eventRows.map(([label, value]) => (
                <CitationRow key={label} label={label} value={value} />
              ))}
            </>
          )}

        </div>
      )}
    </div>
  );
}

// ── Tabs ──────────────────────────────────────────────────────────────────────

const TABS = [
  { id:"overview",      label:"Overview" },
  { id:"products",      label:"Products & Markets" },
  { id:"event-intel",   label:"Event Intelligence" },
  { id:"intelligence",  label:"Global Footprint" },
];

// ── Event Fit Row (accordion) ─────────────────────────────────────────────────

function parseStructuredReasoning(text) {
  if (!text) return null;
  const fit    = text.match(/Fit:\s*([\s\S]*?)(?=\n\nEvidence:|Evidence:|$)/i)?.[1]?.trim();
  const evid   = text.match(/Evidence:\s*([\s\S]*?)(?=\n\nRecommended Action:|Recommended Action:|$)/i)?.[1]?.trim();
  const action = text.match(/Recommended Action:\s*([\s\S]*?)(?=\n\n|$)/i)?.[1]?.trim();
  if (!fit && !evid && !action) return null;
  return { fit, evidence: evid, action };
}

const ACTION_COLORS = {
  "prioritise outreach":      { bg:"#dcfce7", color:"#16a34a" },
  "outreach with conditions": { bg:"#fef4e6", color:"#C9760A" },
  "monitor for next cycle":   { bg:"#FCEBE5", color:"#0369a1" },
  "skip":                     { bg:"#fdecec", color:"#C1121F" },
};
function actionStyle(text) {
  if (!text) return { bg:"#F4F4F5", color:"#818084" };
  const key = Object.keys(ACTION_COLORS).find(k => text.toLowerCase().includes(k));
  return ACTION_COLORS[key] || { bg:"#F4F4F5", color:"#818084" };
}

function EventFitRow({ row, defaultOpen, isLast, feedback }) {
  const [open, setOpen] = React.useState(defaultOpen);
  const hasReasoning = row.reasoning && !EMPTY_STRS.has(String(row.reasoning).toLowerCase().trim());
  const parsed = hasReasoning ? parseStructuredReasoning(row.reasoning) : null;

  return (
    <div style={{ borderBottom: isLast ? "none" : "1px solid #F4F4F5" }}>
      <div
        onClick={() => hasReasoning && setOpen(o => !o)}
        style={{ display:"flex", alignItems:"center", padding:"12px 16px",
          background: open && hasReasoning ? "#FAFAFA" : "#fff",
          cursor: hasReasoning ? "pointer" : "default",
          transition:"background 0.1s" }}
        onMouseEnter={e => { if (hasReasoning) e.currentTarget.style.background = "#FAFAFA"; }}
        onMouseLeave={e => { e.currentTarget.style.background = open && hasReasoning ? "#FAFAFA" : "#fff"; }}
      >
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:13, fontWeight:700, color:"#26262A" }}>{row.name}</span>
            {row.isPrimary && (
              <span style={{ fontSize:8, fontWeight:800, color:"#E8431A",
                background:"#FCEBE5", padding:"1px 7px", borderRadius:10,
                letterSpacing:0.5, textTransform:"uppercase", flexShrink:0 }}>★ primary</span>
            )}
          </div>
          <div style={{ fontSize:11, color:"#818084", marginTop:2 }}>
            {row.category}
            {row.subcategory && row.subcategory !== row.category && (
              <>
                <span style={{ color:"#D8DADC", margin:"0 5px" }}>›</span>
                <span style={{ color:"#818084" }}>{row.subcategory}</span>
              </>
            )}
          </div>
        </div>
        {/* Fit level */}
        <div style={{ width:80, textAlign:"right", fontSize:12, fontWeight:600,
          color: FIT_COLOR[row.fit] || "#818084" }}>
          {capitalize(row.fit) || "—"}
        </div>
        {/* Human-review vote on this event match */}
        {feedback && <div style={{ marginLeft:14 }}>{feedback}</div>}
        {/* Toggle */}
        <div style={{ width:32, textAlign:"right", marginLeft:8 }}>
          {hasReasoning && (
            <span style={{ display:"inline-flex", alignItems:"center", justifyContent:"center",
              width:22, height:22, borderRadius:11, background:"#F4F4F5",
              fontSize:13, color:"#818084", lineHeight:1 }}>
              {open ? "−" : "+"}
            </span>
          )}
        </div>
      </div>

      {/* Reasoning panel */}
      {open && hasReasoning && (
        <div style={{ margin:"0 16px 12px" }}>
          {parsed ? (
            /* Structured: Fit · Evidence · Action */
            <div style={{ border:"1px solid #D8DADC", borderRadius:8, overflow:"hidden" }}>
              {parsed.fit && (
                <div style={{ display:"flex", gap:12, padding:"10px 14px",
                  borderBottom:"1px solid #F4F4F5", background:"#fafafa" }}>
                  <span style={{ fontSize:9, fontWeight:700, color:"#818084",
                    textTransform:"uppercase", letterSpacing:0.5, width:64,
                    flexShrink:0, paddingTop:2 }}>Fit</span>
                  <span style={{ fontSize:11, color:"#3F3F46", lineHeight:1.65 }}>{parsed.fit}</span>
                </div>
              )}
              {parsed.evidence && (
                <div style={{ display:"flex", gap:12, padding:"10px 14px",
                  borderBottom:"1px solid #F4F4F5", background:"#fafafa" }}>
                  <span style={{ fontSize:9, fontWeight:700, color:"#818084",
                    textTransform:"uppercase", letterSpacing:0.5, width:64,
                    flexShrink:0, paddingTop:2 }}>Evidence</span>
                  <span style={{ fontSize:11, color:"#3F3F46", lineHeight:1.65 }}>{parsed.evidence}</span>
                </div>
              )}
              {parsed.action && (
                <div style={{ display:"flex", gap:12, padding:"10px 14px",
                  alignItems:"center", background:"#fff" }}>
                  <span style={{ fontSize:9, fontWeight:700, color:"#818084",
                    textTransform:"uppercase", letterSpacing:0.5, width:64, flexShrink:0 }}>Action</span>
                  <span style={{ fontSize:11, fontWeight:700, padding:"4px 14px",
                    borderRadius:20, ...actionStyle(parsed.action) }}>
                    {parsed.action}
                  </span>
                </div>
              )}
            </div>
          ) : (
            /* Plain text fallback for secondary events */
            <div style={{ padding:"10px 14px", background:"#FAFAFA",
              border:"1px solid #D8DADC", borderRadius:8,
              fontSize:11, color:"#3F3F46", lineHeight:1.7 }}>
              {row.reasoning}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── AI feedback (thumbs up / down) ────────────────────────────────────────────

// Human-review vote on whether an AI event match is correct. One control per
// event row; safe to click inside the accordion (stops row-toggle propagation).
function FeedbackButtons({ companyId, showId, eventId, target = "event_fit", createdBy, createdRole, onToast }) {
  const [rating, setRating]           = useState(null);
  const [comment, setComment]         = useState("");
  const [showComment, setShowComment] = useState(false);

  useEffect(() => {
    let alive = true;
    setRating(null); setComment(""); setShowComment(false);
    getFeedback({ company_id: companyId, target, ite_event_id: eventId || "", created_by: createdBy })
      .then(d => { if (alive) { setRating(d.my_rating || null); setComment(d.my_comment || ""); } })
      .catch(() => {});
    return () => { alive = false; };
  }, [companyId, target, eventId, createdBy]);

  const save = (next, note) => {
    const prev = rating;
    setRating(next);                                    // optimistic
    upsertFeedback({
      company_id: companyId, show_id: showId, ite_event_id: eventId, target,
      rating: next, comment: next === "down" ? (note ?? comment) || null : null,
      created_by: createdBy, created_role: createdRole,
    })
      .then(() => onToast?.(next ? "Feedback saved" : "Feedback cleared"))
      .catch(() => { setRating(prev); onToast?.("Couldn't save feedback", "error"); });
  };

  const clickUp = (e) => {
    e.stopPropagation();
    setShowComment(false);
    save(rating === "up" ? null : "up");
  };
  const clickDown = (e) => {
    e.stopPropagation();
    if (rating === "down") { setShowComment(false); save(null); }
    else { setShowComment(true); save("down"); }
  };

  const btn = (active, activeColor) => ({
    display:"inline-flex", alignItems:"center", justifyContent:"center",
    width:26, height:26, borderRadius:6, cursor:"pointer",
    border:`1px solid ${active ? activeColor : "#D8DADC"}`,
    background: active ? `${activeColor}14` : "#fff",
    color: active ? activeColor : "#AFABAB", padding:0, lineHeight:0,
    transition:"all 0.12s",
  });

  return (
    <div style={{ position:"relative", display:"inline-flex", alignItems:"center", gap:5 }}
      onClick={e => e.stopPropagation()}>
      <button title="Correct match" onClick={clickUp} style={btn(rating === "up", "#16a34a")}>
        <ThumbsUp size={13}/>
      </button>
      <button title="Wrong match" onClick={clickDown} style={btn(rating === "down", "#C1121F")}>
        <ThumbsDown size={13}/>
      </button>
      {showComment && rating === "down" && (
        <div style={{ position:"absolute", top:"calc(100% + 6px)", right:0, zIndex:30 }}>
          <input
            autoFocus
            value={comment}
            onChange={e => setComment(e.target.value)}
            onBlur={() => { save("down", comment); setShowComment(false); }}
            onKeyDown={e => { if (e.key === "Enter") { save("down", comment); setShowComment(false); } }}
            placeholder="What's wrong? (optional)"
            style={{ width:230, fontSize:11, border:"1px solid #fbd5d5", borderRadius:6,
              padding:"6px 9px", outline:"none", color:"#26262A", background:"#fdecec",
              boxShadow:"0 6px 16px rgba(0,0,0,0.12)", fontFamily:"inherit" }}
          />
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function CompanyDetailPage() {
  const {
    nav, selectedEvent, changeEvent,
    currentRole, setCurrentRole,
    currentUser,
    notifs, setNotifs, notifOpen, setNotifOpen,
    selectedCompanyId, showToast,
    leadReviewCtx, setLeadReviewCtx,
    setPendingLeadsShow,
    requestDecision: sharedRequestDecision, updateReviewStatus, reviewCompanies,
  } = useApp();

  const [co, setCo]           = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openSections, setOpenSections] = useState(new Set(["overview"]));
  const [editMode, setEditMode]         = useState(false);
  const [draft, setDraft]               = useState({});
  const [contactDraft, setContactDraft] = useState([]);  // contact rows in edit mode
  const [saving, setSaving]             = useState(false);
  const [confirmSave, setConfirmSave]       = useState(false); // true = awaiting user confirmation
  const [contactsLoadFailed, setContactsLoadFailed] = useState(false); // true = contacts API failed on load
  const contactKeyRef                       = useRef(0);     // stable keys for new contact rows
  const [aiMenuOpen, setAiMenuOpen] = useState(false);

  useEffect(() => {
    if (!selectedCompanyId) { nav("companies"); return; }
    setLoading(true);
    setContacts([]);
    setContactsLoadFailed(false);
    getCompany(selectedCompanyId)
      .then(data => {
        setCo(data);
        setLoading(false);
        getContacts(selectedCompanyId, data.show_id)
          .then(rows => setContacts(rows || []))
          .catch(() => {
            setContactsLoadFailed(true);
            showToast("Contacts failed to load — contact editing disabled until reload", "error");
          });
      })
      .catch(() => { showToast("Failed to load company profile", "error"); setLoading(false); });
  }, [selectedCompanyId]);

  const setField = (key, val) => setDraft(d => ({ ...d, [key]: val }));

  // Contact draft helpers — used by the inline contact list editor in edit mode
  const addContact = (kind) =>
    setContactDraft(d => [...d, {
      _key:          `new_${++contactKeyRef.current}`,
      contact_id:    null,
      contact_kind:  kind,
      contact_type:  "generic",
      contact_value: "",
    }]);
  const updateContact = (key, field, val) =>
    setContactDraft(d => d.map(c => c._key === key ? { ...c, [field]: val } : c));
  const removeContact = (key) =>
    setContactDraft(d => d.filter(c => c._key !== key));

  const toComma = (s) => (s || "").replace(/\|/g, ", ");
  const toPipe  = (s) => (s || "").split(",").map(x => x.trim()).filter(Boolean).join("|");

  const startEdit = () => {
    setDraft({
      hq_country:           co.hq_country          || "",
      hq_city:              co.hq_city             || "",
      physical_address:     co.physical_address    || "",
      website:              co.website             || "",
      company_type:         co.company_type        || "",
      country_region:       co.country_region      || "",
      country_status:       co.country_status      || "",
      hq_type:              co.hq_type             || "",
      parent_company:       co.parent_company      || "",
      parent_country:       co.parent_country      || "",
      website_languages:    co.website_languages   || "",
      core_products:        toComma(co.core_products),
      target_customers:     toComma(co.target_customers),
      target_market:        toComma(co.target_market),
      certifications:       toComma(co.certifications),
      distributor_presence: co.distributor_presence || "",
    });
    // Block edit mode if contacts failed to load — editing with an empty draft
    // would soft-delete all existing contacts on save.
    if (contactsLoadFailed) {
      showToast("Cannot edit — contacts failed to load. Please reload the page.", "error");
      return;
    }
    // Seed contact editor from the already-loaded contacts state.
    // contact_id is the stable React key for existing rows.
    setContactDraft(contacts.map(c => ({ ...c, _key: c.contact_id })));
    setEditMode(true);
  };

  const cancelEdit = () => { setEditMode(false); setDraft({}); setContactDraft([]); setConfirmSave(false); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      // ── 1. Firmographics / market profile ─────────────────────────────────
      // Normalise pipe-separated tag fields before diffing.
      const normalised = {
        ...draft,
        core_products:    toPipe(draft.core_products),
        target_customers: toPipe(draft.target_customers),
        target_market:    toPipe(draft.target_market),
        certifications:   toPipe(draft.certifications),
      };
      const PIPE_FIELDS = new Set(["core_products", "target_customers", "target_market", "certifications"]);
      const changed = {};
      for (const [k, v] of Object.entries(normalised)) {
        const base = PIPE_FIELDS.has(k) ? toPipe(toComma(co[k] ?? "")) : (co[k] ?? "");
        if ((v ?? "") !== base) changed[k] = v;
      }
      if (Object.keys(changed).length > 0) {
        await patchCompany(selectedCompanyId, { patched_by: currentUser || currentRole, ...changed });
      }

      // ── 2. Contacts ────────────────────────────────────────────────────────
      // Contacts require a show_id to scope the update. Companies without a
      // linked show cannot have structured contacts — skip with a warning.
      if (co.show_id) {
        await patchContacts(selectedCompanyId, {
          show_id:    co.show_id,
          patched_by: currentUser || currentRole,
          contacts:   contactDraft
            .filter(c => c.contact_value?.trim())   // drop blank/empty rows
            .map(({ _key, ...rest }) => rest),       // strip client-side key
        });
      } else if (contactDraft.length > 0) {
        showToast("Contact changes not saved — this company has no linked show", "error");
      }

      // ── 3. Close edit mode ─────────────────────────────────────────────────
      setEditMode(false);
      setDraft({});
      setContactDraft([]);
      setConfirmSave(false);
      showToast("Profile updated");

      // ── 4. Refresh UI — failures here are cosmetic, data is already saved ──
      try {
        const [updatedCo, updatedContacts] = await Promise.all([
          getCompany(selectedCompanyId),
          co.show_id ? getContacts(selectedCompanyId, co.show_id) : Promise.resolve(null),
        ]);
        setCo(updatedCo);
        if (updatedContacts !== null) setContacts(updatedContacts || []);
      } catch {
        // Data is persisted; UI may be slightly stale until next page load.
      }
    } catch {
      setConfirmSave(false);
      showToast("Save failed — check backend", "error");
    } finally {
      setSaving(false);
    }
  };

  // Overview is always open. For all other sections: opening one closes the rest.
  const toggleSection = (id) => {
    setOpenSections(prev => {
      const next = new Set(["overview"]);
      if (!prev.has(id)) next.add(id); // open if closed; leave closed if already open
      return next;
    });
  };

  const scrollToSection = (id) => {
    setOpenSections(() => {
      const next = new Set(["overview"]);
      if (id !== "overview") next.add(id);
      return next;
    });
    setTimeout(() => {
      document.getElementById(`sec-${id}`)?.scrollIntoView({ behavior:"smooth", block:"start" });
    }, 50);
  };

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ display:"flex", height:"100vh", fontFamily:F }}>
        <Sidebar active="companies" onNav={nav} selectedEvent={selectedEvent}
          onChangeEvent={changeEvent} notifs={notifs} setNotifs={setNotifs}
          notifOpen={notifOpen} setNotifOpen={setNotifOpen}
          currentRole={currentRole} setCurrentRole={setCurrentRole}/>
        <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center",
          flexDirection:"column", gap:12, color:"#818084" }}>
          <RefreshCw size={28}/>
          <div style={{ fontSize:13 }}>Loading company profile…</div>
        </div>
      </div>
    );
  }

  if (!co) return null;

  // ── Derived values ───────────────────────────────────────────────────────────
  const flag            = getFlag(co.hq_country);
  const priScore        = co.priority_score ?? null;
  const priColor        = priScore >= 70 ? "#16a34a" : priScore >= 50 ? "#C9760A" : "#C1121F";
  const location        = [co.hq_city, co.hq_country].filter(Boolean).join(", ");
  const coreProducts    = splitCSV(co.core_products);
  const targetCustomers = splitCSV(co.target_customers);
  const targetMarkets   = splitCSV(co.target_market);
  const certifications  = splitCSV(co.certifications);
  const pastEvents      = splitCSV(co.past_events);
  const eventRegions    = splitCSV(co.event_locations || co.event_regions);

  // event_fits from API — split into primary vs secondary
  const allEventFits    = Array.isArray(co.event_fits) ? co.event_fits : [];
  const primaryFit      = allEventFits.find(ef => ef.is_primary) || null;
  const secondaryFits   = allEventFits.filter(ef => !ef.is_primary);

  // Per competitor-show edition, not the company's history with ITE — the data
  // records exhibitors at competitor shows, so "New to ITE" was unsupportable.
  const iteStatus    = co.is_new ? "New" : co.is_returning ? "Returning" : "—";
  // Uses the same emptiness test as InfoRow, so the badge cannot disagree with the
  // value shown next to it — "unknown" and "not found" count as absent, not as a URL.
  const hasWebsite   = co.website != null
    && !EMPTY_STRS.has(String(co.website).toLowerCase().trim());
  const websiteHref  = hasWebsite
    ? "https://" + co.website.replace(/^https?:\/\//, "")
    : null;
  const linkedinHref = co.linkedin_page
    ? (co.linkedin_page.startsWith("http") ? co.linkedin_page : "https://" + co.linkedin_page)
    : null;

  // ── Provenance: which competitor show this exhibitor was scraped from, and which
  // ITE event it's matched to. Read from the loaded record (not the nav context,
  // which is event-agnostic when arriving from Company Master). ──
  const primaryEventName = primaryFit?.event_name || co.primary_event_name || null;
  const showYear         = (co.show_id && co.show_id.match(/_(\d{4})$/)?.[1]) || null;

  return (
    <ShowWebsiteContext.Provider value={co.show_website}>
    <div style={{ display:"flex", height:"100vh", fontFamily:F,
      background:"#F4F4F5", color:"#26262A", overflow:"hidden" }}>
      <style>{`
        *::-webkit-scrollbar{width:5px}
        *::-webkit-scrollbar-thumb{background:#D8DADC;border-radius:3px}
        .ei-row:hover{background:#FAFAFA}
      `}</style>

      <Sidebar active="companies" onNav={nav} selectedEvent={selectedEvent}
        onChangeEvent={changeEvent} notifs={notifs} setNotifs={setNotifs}
        notifOpen={notifOpen} setNotifOpen={setNotifOpen}
        currentRole={currentRole} setCurrentRole={setCurrentRole}/>

      <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>

        {/* ── Breadcrumb ─────────────────────────────────────────────────────── */}
        <div style={{ padding:"10px 28px", background:"#fff",
          borderBottom:"1px solid #D8DADC", display:"flex", alignItems:"center", gap:6,
          flexShrink:0 }}>
          <span onClick={() => { if (editMode) return; if (leadReviewCtx) { setPendingLeadsShow({ show_id: leadReviewCtx.asgnId, tab: leadReviewCtx.isMH ? "campaignlists" : "mylists" }); setLeadReviewCtx(null); nav("leads"); } else { nav("companies"); } }}
            style={{ fontSize:12, color: editMode ? "#818084" : "#E8431A",
              cursor: editMode ? "default" : "pointer", fontWeight:500 }}>
            {leadReviewCtx ? "← Back to Review" : "Company Master"}
          </span>
          {/* Provenance inline: target ITE event › source competitor show · edition */}
          {primaryEventName && (
            <>
              <span style={{ fontSize:12, color:"#D8DADC" }}>›</span>
              <span title="Target ITE event" style={{ fontSize:12, color:"#818084" }}>
                {primaryEventName}
              </span>
            </>
          )}
          {co.show_name && (
            <>
              <span style={{ fontSize:12, color:"#D8DADC" }}>›</span>
              <span title="Source competitor show" style={{ fontSize:12, color:"#818084" }}>
                {co.show_name}
                {showYear && <span style={{ color:"#AFABAB" }}> · {showYear}</span>}
              </span>
            </>
          )}
          <span style={{ fontSize:12, color:"#D8DADC" }}>›</span>
          <span style={{ fontSize:12, color:"#26262A", fontWeight:600 }}>{co.company_name}</span>
          {leadReviewCtx?.isMql && (
            <span style={{ fontSize:9, fontWeight:700, color:"#960659", background:"#F6E6EF", padding:"2px 8px", borderRadius:4, border:"1px solid #a5f3fc" }}>MQL</span>
          )}
          {editMode && (
            <span style={{ fontSize:11, color:"#F18F18", fontWeight:600,
              background:"#fef4e6", borderRadius:4, padding:"2px 8px", marginLeft:4 }}>
              editing
            </span>
          )}
          <div style={{ flex:1 }} />
          {!editMode ? (
            <button onClick={startEdit}
              style={{ fontSize:11, fontWeight:600, color:"#3F3F46",
                background:"#FAFAFA", border:"1px solid #D8DADC",
                borderRadius:6, padding:"5px 14px", cursor:"pointer", fontFamily:F,
                display:"flex", alignItems:"center", gap:5 }}>
              ✏ Edit Profile
            </button>
          ) : (
            <div style={{ display:"flex", gap:8 }}>
              {confirmSave ? <>
                <span style={{ fontSize:11, color:"#26262A", fontWeight:500 }}>
                  Save changes to this profile?
                </span>
                <button onClick={cancelEdit}
                  style={{ fontSize:11, fontWeight:600, color:"#818084",
                    background:"#FAFAFA", border:"1px solid #D8DADC",
                    borderRadius:6, padding:"5px 14px", cursor:"pointer", fontFamily:F }}>
                  No, cancel
                </button>
                <button onClick={saveEdit} disabled={saving}
                  style={{ fontSize:11, fontWeight:700, color:"#fff",
                    background: saving ? "#F3A78C" : "#E8431A",
                    border:"none", borderRadius:6, padding:"5px 18px",
                    cursor: saving ? "default" : "pointer", fontFamily:F }}>
                  {saving ? "Saving…" : "Yes, save"}
                </button>
              </> : <>
                <button onClick={cancelEdit}
                  style={{ fontSize:11, fontWeight:600, color:"#818084",
                    background:"#FAFAFA", border:"1px solid #D8DADC",
                    borderRadius:6, padding:"5px 14px", cursor:"pointer", fontFamily:F }}>
                  Cancel
                </button>
                <button onClick={() => setConfirmSave(true)}
                  style={{ fontSize:11, fontWeight:700, color:"#fff",
                    background:"#E8431A", border:"none", borderRadius:6,
                    padding:"5px 18px", cursor:"pointer", fontFamily:F }}>
                  Save changes
                </button>
              </>}
            </div>
          )}
        </div>

        {/* ── Tab bar ───────────────────────────────────────────────────────── */}
        <div style={{ display:"flex", background:"#fff", borderBottom:"1px solid #D8DADC", flexShrink:0, paddingLeft:20 }}>
          {TABS.map(t => {
            const isOpen = openSections.has(t.id);
            return (
              <button key={t.id} onClick={() => scrollToSection(t.id)}
                style={{ padding:"9px 16px", border:"none", cursor:"pointer",
                  fontFamily:F, fontSize:12,
                  fontWeight: isOpen ? 700 : 400,
                  color: isOpen ? "#26262A" : "#818084",
                  background:"transparent",
                  borderBottom: isOpen ? "2px solid #E8431A" : "2px solid transparent",
                  transition:"all 0.15s" }}>
                {t.label}
              </button>
            );
          })}
        </div>

        {/* ── Scrollable content ────────────────────────────────────────────── */}
        <div style={{ flex:1, overflow:"auto", padding:"20px 28px 48px" }}>

          {/* AI disclaimer — this profile is auto-enriched from public sources */}
          <div style={{
            display:"flex", alignItems:"center", gap:8,
            padding:"7px 12px", marginBottom:16,
            background:"#F9FAFB", border:"1px solid #E5E7EB",
            borderRadius:6, fontSize:11, color:"#6B7280", lineHeight:1.45,
          }}>
            <Info size={13} color="#9CA3AF" style={{ flexShrink:0 }} />
            <span>
              <b style={{ color:"#374151" }}>AI-enriched profile.</b>
              {" "}Auto-generated from public sources — details may be incomplete or outdated. Verify before outreach or decisions.
            </span>
          </div>

          {/* ── 1. Overview ───────────────────────────────────────────────── */}
          <SectionCard title="Overview" id="sec-overview" open alwaysOpen>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 56px" }}>
              <div>
                <SubLabel>Firmographics</SubLabel>
                {editMode ? <>
                  <ERow label="HQ Country"       field="hq_country"       draft={draft} onChange={setField} source={co.hq_country_source} />
                  <ERow label="HQ City"          field="hq_city"          draft={draft} onChange={setField} source={co.hq_city_source} />
                  <ERow label="Physical Address" field="physical_address" draft={draft} onChange={setField} source={co.physical_address_source} />
                  <ERow label="Website"          field="website"          draft={draft} onChange={setField} />
                  <ERow label="Company Type"     field="company_type"     draft={draft} onChange={setField} source={co.company_type_citation} />
                  <ERow label="Region"           field="country_region"   draft={draft} onChange={setField} />
                  <ESelectRow label="Country Status" field="country_status" draft={draft} onChange={setField}
                    options={["Friendly", "Unfriendly", "Unknown"]} />
                  <ESelectRow label="Entity Type" field="hq_type" draft={draft} onChange={setField}
                    options={["Global HQ", "Subsidiary", "Branch", "Regional Office", "Unknown"]} />
                  <ERow label="Parent Company"  field="parent_company"  draft={draft} onChange={setField} />
                  <ERow label="Parent Country"  field="parent_country"  draft={draft} onChange={setField} />
                </> : <>
                  <InfoRow label="HQ Country"      value={co.hq_country}      source={co.hq_country_source}
                    badge={<CountryStatusTag status={co.country_status} />} />
                  <InfoRow label="HQ City"         value={co.hq_city}         source={co.hq_city_source} />
                  <InfoRow label="Physical Address" value={co.physical_address} source={co.physical_address_source} />
                  <InfoRow label="Website"         value={co.website}         href={websiteHref}
                    badge={!hasWebsite && <NoWebsiteTag />}
                    note={hasWebsite ? null
                      : "Enriched without a company website — the fields below come from an "
                        + "open web search and the LinkedIn page, so confirm this is the right "
                        + "company before acting on them."} />
                  <InfoRow label="Company Type"    value={co.company_type}    source={co.company_type_citation}
                    note={co.company_type_reasoning} />
                  <InfoRow label="Region"          value={co.country_region} />
                  <InfoRow label="Entity Type"      value={co.hq_type} source={co.hq_type_citation || co.hq_type_source}
                    note={co.hq_type_reasoning}
                    hint="Global HQ · Subsidiary · Branch · Regional Office" />
                  {co.parent_company && co.parent_company !== "N/A" && (
                    <InfoRow label="Parent Company"  value={co.parent_company} />
                  )}
                  {co.parent_country && co.parent_country !== "N/A" && (
                    <InfoRow label="Parent Country"  value={co.parent_country} />
                  )}
                </>}
              </div>
              <div>
                <SubLabel>Contact</SubLabel>
                {editMode ? (() => {
                  const KIND_META = [
                    { kind: "email",    label: "Email",       multi: true  },
                    { kind: "phone",    label: "Phone",       multi: false },
                    { kind: "linkedin", label: "LinkedIn",    multi: false },
                    { kind: "social",   label: "Other Social",multi: false },
                  ];
                  return KIND_META.map(({ kind, label, multi }) => {
                    const rows = contactDraft.filter(c => c.contact_kind === kind);
                    return (
                      <div key={kind} style={{
                        display:"flex", gap:16, padding:"7px 0",
                        borderBottom:"1px solid #F4F4F5", alignItems:"flex-start",
                      }}>
                        <div style={{ width:150, flexShrink:0, fontSize:11, color:"#818084", fontWeight:500, paddingTop:7 }}>
                          {label}
                        </div>
                        <div style={{ flex:1 }}>
                          {rows.map(c => (
                            <div key={c._key} style={{ display:"flex", gap:6, marginBottom:6, alignItems:"center" }}>
                              {kind === "email" && (
                                <select
                                  value={c.contact_type}
                                  onChange={e => updateContact(c._key, "contact_type", e.target.value)}
                                  style={{ fontSize:11, border:"1px solid #F7C9B8", borderRadius:6,
                                    padding:"6px 6px", background:"#f0f7ff", color:"#26262A", cursor:"pointer" }}>
                                  <option value="generic">Generic</option>
                                  <option value="functional">Functional</option>
                                </select>
                              )}
                              <input
                                value={c.contact_value || ""}
                                onChange={e => updateContact(c._key, "contact_value", e.target.value)}
                                placeholder={`${label} address`}
                                style={inputStyle}
                              />
                              <button
                                onClick={() => removeContact(c._key)}
                                title={`Remove ${label.toLowerCase()}`}
                                style={{ background:"none", border:"none", color:"#9CA3AF",
                                  cursor:"pointer", fontSize:18, padding:"0 4px", lineHeight:1 }}>
                                ×
                              </button>
                            </div>
                          ))}
                          {(multi || rows.length === 0) && (
                            <button
                              onClick={() => addContact(kind)}
                              style={{ background:"none", border:"none", color:"#E8431A",
                                cursor:"pointer", fontSize:11, padding:"3px 0", fontWeight:500 }}>
                              + Add {label.toLowerCase()}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  });
                })() : (() => {
                  const emailRows    = contacts.filter(c => c.contact_kind === "email");
                  const phoneRows    = contacts.filter(c => c.contact_kind === "phone");
                  const linkedinRow  = contacts.find(c => c.contact_kind === "linkedin");
                  const socialRow    = contacts.find(c => c.contact_kind === "social");
                  const personalOnly = emailRows.length === 1 && emailRows[0].contact_type === "personal_only";
                  const usableEmails = emailRows.filter(c => c.contact_type !== "personal_only");
                  // Origins across the shown contacts → legend once + a dot per row.
                  const shownSources = [...usableEmails, ...phoneRows, socialRow].filter(Boolean).map(c => c.source);
                  const originsPresent = Object.keys(CONTACT_SOURCES).filter(
                    k => shownSources.some(s => contactOrigin(s) === k)
                  );
                  return <>
                    <div style={{
                      display:"flex", alignItems:"center", gap:5,
                      marginBottom:8, fontSize:10, color:"#9CA3AF", lineHeight:1.4,
                    }}>
                      <Info size={11} color="#D1D5DB" style={{ flexShrink:0 }} />
                      <span>Only generic/functional emails stored. Personal details available via source links. <b style={{ color:"#E8431A" }}>Verify contacts before outreach.</b></span>
                    </div>
                    <ContactLegend origins={originsPresent} />
                    {usableEmails.length > 0 ? usableEmails.map((c, i) => (
                      <InfoRow key={c.contact_id || i}
                        label={i === 0 ? "Email" : ""}
                        value={c.contact_value}
                        href={`mailto:${c.contact_value}`}
                        source={c.source}
                        origin={contactOrigin(c.source)} />
                    )) : personalOnly ? (
                      <InfoRow label="Email" value="Personal emails only"
                        source={emailRows[0]?.source || co.website} />
                    ) : (
                      <InfoRow label="Email" value={co.email || null}
                        href={co.email ? `mailto:${co.email.replace(/\[(?:generic|functional)\]\s*/g, "")}` : null}
                        source={co.email_source} />
                    )}
                    {phoneRows.length > 0 ? phoneRows.map((c, i) => (
                      <InfoRow key={c.contact_id || `ph${i}`}
                        label={i === 0 ? "Phone" : ""}
                        value={formatPhoneValue(c.contact_value)} mono
                        source={c.source}
                        origin={contactOrigin(c.source)} />
                    )) : (
                      <InfoRow label="Phone" value={co.phone || null} mono source={co.phone_source} />
                    )}
                    <InfoRow label="LinkedIn"
                      value={linkedinRow ? linkedinRow.contact_value : co.linkedin_page}
                      href={linkedinRow ? linkedinRow.contact_value : linkedinHref} />
                    {socialRow && (
                      <InfoRow label="Other Social" value={socialRow.contact_value} source={socialRow.source}
                        href={socialRow.contact_value?.startsWith("http") ? socialRow.contact_value : null} />
                    )}
                    {!socialRow && co.social_media && (
                      <InfoRow label="Other Social" value={co.social_media} source={co.social_media_source}
                        href={co.social_media.startsWith("http") || co.social_media.startsWith("www.")
                          ? toHref(co.social_media) : null} />
                    )}
                  </>;
                })()}
              </div>
            </div>
          </SectionCard>

          {/* ── 2. Products & Markets ─────────────────────────────────────── */}
          <SectionCard title="Products & Markets" id="sec-products" open={openSections.has("products")} onToggle={() => toggleSection("products")}>
            {editMode && (
              <div style={{ fontSize:11, color:"#818084", marginBottom:16,
                padding:"8px 12px", background:"#f0f7ff", borderRadius:7,
                border:"1px solid #FCEBE5" }}>
                Enter values separated by commas. A live preview of the tags updates below each field.
              </div>
            )}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 56px" }}>
              <div>
                {editMode ? <>
                  <ETagRow label="Core Products"    field="core_products"    draft={draft} onChange={setField} palette="products"  source={co.core_products_source} />
                  <ETagRow label="Target Customers" field="target_customers" draft={draft} onChange={setField} palette="customers" source={co.target_customers_source} />
                </> : <>
                  <TagGroup label="Core Products"    items={coreProducts}    palette="products"  source={co.core_products_source} />
                  <TagGroup label="Target Customers" items={targetCustomers} palette="customers" source={co.target_customers_source} />
                </>}
              </div>
              <div>
                {editMode ? <>
                  <ETagRow label="Target Markets"  field="target_market"  draft={draft} onChange={setField} palette="markets" source={co.target_market_source} />
                  <ETagRow label="Certifications"  field="certifications" draft={draft} onChange={setField} palette="certs"   source={co.certifications_source} />
                </> : <>
                  <TagGroup label="Target Markets"   items={targetMarkets}   palette="markets"   source={co.target_market_source} />
                  <TagGroup label="Certifications"   items={certifications}  palette="certs"     source={co.certifications_source} />
                </>}
              </div>
            </div>
          </SectionCard>

          {/* ── 3. Event Intelligence ─────────────────────────────────────── */}
          <SectionCard title="Event Intelligence" id="sec-event-intel" open={openSections.has("event-intel")} onToggle={() => toggleSection("event-intel")}>

            {allEventFits.length === 0 && !co.primary_event_category ? (
              <div style={{ textAlign:"center", padding:"32px 0", color:"#D8DADC", fontSize:12 }}>
                No event match data available
              </div>
            ) : (() => {
              const primaryRow = (primaryFit || co.primary_event_category) ? {
                name:        primaryFit?.event_name || co.primary_event_name || "Primary Event",
                category:    primaryFit?.category    || co.primary_event_category    || "—",
                subcategory: primaryFit?.subcategory || co.primary_event_subcategory || null,
                fit:         primaryFit?.event_fit   || co.primary_event_fit,
                reasoning:   co.priority_reason || primaryFit?.reasoning || co.primary_event_fit_logic || null,
                eventId:     primaryFit?.ite_event_id || co.primary_event_id || null,
                isPrimary:   true,
              } : null;

              const secondaryRows = secondaryFits.map(ef => ({
                name:        ef.event_name || ef.ite_event_id || "—",
                category:    ef.category    || "—",
                subcategory: ef.subcategory || null,
                fit:         ef.event_fit   || ef.fit,
                reasoning:   ef.reasoning   || null,
                eventId:     ef.ite_event_id || null,
                isPrimary:   false,
              }));

              // Human-review vote on whether the AI matched this event correctly
              const voteFor = (row) => (
                <FeedbackButtons
                  companyId={co.company_id}
                  showId={co.show_id}
                  eventId={row.eventId}
                  target="event_fit"
                  createdBy={currentUser || currentRole}
                  createdRole={currentRole}
                  onToast={showToast}
                />
              );

              const colHeader = (
                <div style={{ display:"flex", alignItems:"center", padding:"7px 16px",
                  background:"#FAFAFA", borderBottom:"1px solid #D8DADC" }}>
                  <div style={{ flex:1, fontSize:9, fontWeight:700, color:"#818084",
                    textTransform:"uppercase", letterSpacing:0.5 }}>Event / Category</div>
                  <div style={{ width:80, textAlign:"right", fontSize:9, fontWeight:700,
                    color:"#818084", textTransform:"uppercase", letterSpacing:0.5 }}>Fit</div>
                  {/* matches the per-row feedback control (14px margin + 2×26px + 5px gap) */}
                  <div style={{ width:71 }} />
                  <div style={{ width:40 }} />
                </div>
              );

              return (
                <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

                  {/* ── Target Event ── */}
                  {primaryRow && (
                    <div>
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                        <div style={{ width:10, height:10, borderRadius:2, background:"#16a34a" }} />
                        <span style={{ fontSize:10, fontWeight:700, color:"#16a34a",
                          textTransform:"uppercase", letterSpacing:0.6 }}>Target Event</span>
                      </div>
                      <div style={{ border:"1px solid #d1fae5", borderRadius:10, overflow:"hidden" }}>
                        {colHeader}
                        <EventFitRow row={primaryRow} defaultOpen={true} isLast={true}
                          feedback={voteFor(primaryRow)} />
                      </div>
                    </div>
                  )}

                  {/* ── Cross-Event Opportunities ── */}
                  {secondaryRows.length > 0 && (
                    <div>
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                        <div style={{ width:10, height:10, borderRadius:2, background:"#960659" }} />
                        <span style={{ fontSize:10, fontWeight:700, color:"#960659",
                          textTransform:"uppercase", letterSpacing:0.6 }}>
                          Cross-Event Opportunities
                        </span>
                        <span style={{ fontSize:10, color:"#818084" }}>· {secondaryRows.length} events</span>
                      </div>
                      <div style={{ border:"1px solid #D8DADC", borderRadius:10, overflow:"hidden" }}>
                        {colHeader}
                        {secondaryRows.map((row, i) => (
                          <EventFitRow key={row.eventId || i} row={row} defaultOpen={false}
                            isLast={i === secondaryRows.length - 1}
                            feedback={voteFor(row)} />
                        ))}
                      </div>
                    </div>
                  )}

                </div>
              );
            })()}

          </SectionCard>

          {/* ── 4. Intelligence ───────────────────────────────────────────── */}
          <SectionCard title="Global Footprint" id="sec-intelligence" open={openSections.has("intelligence")} onToggle={() => toggleSection("intelligence")}>
            <div style={{ display:"flex", gap:28, alignItems:"flex-start" }}>

              {/* Map — left ~50% */}
              <div style={{ flex:"0 0 50%", minWidth:0 }}>
                <WorldMap
                  hqCountry={co.hq_country}
                  hqCity={co.hq_city}
                  eventLocations={[...pastEvents, ...eventRegions]}
                  marketLocations={targetMarkets}
                />
              </div>

              {/* Text — right */}
              <div style={{ flex:1, minWidth:0, display:"flex", flexDirection:"column", gap:24 }}>

                {/* ── 1. HQ Country ── */}
                <div>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                    {/* bullseye: outer glow + filled ring + white center — matches map HQ pin */}
                    <span style={{ position:"relative", width:22, height:22, flexShrink:0,
                      display:"inline-flex", alignItems:"center", justifyContent:"center" }}>
                      <span style={{ position:"absolute", width:22, height:22, borderRadius:"50%",
                        background:"#E8431A", opacity:0.2 }}/>
                      <span style={{ position:"absolute", width:14, height:14, borderRadius:"50%",
                        background:"#E8431A", border:"2px solid #fff" }}/>
                      <span style={{ position:"absolute", width:6, height:6, borderRadius:"50%",
                        background:"#fff" }}/>
                    </span>
                    <span style={{ fontSize:10, fontWeight:700, color:"#E8431A",
                      textTransform:"uppercase", letterSpacing:0.6 }}>HQ Country</span>
                  </div>
                  <div style={{ paddingLeft:19 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:"#26262A", marginBottom:4 }}>
                      {co.hq_country || <span style={{ color:"#D8DADC", fontWeight:400 }}>Not available</span>}
                    </div>
                    {co.country_region && (
                      <div style={{ fontSize:11, color:"#818084" }}>{co.country_region}</div>
                    )}
                  </div>
                </div>

                {/* ── 2. Target Markets ── purple, matches map pins */}
                <div>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                    {/* glow + filled dot — matches map market pin */}
                    <span style={{ position:"relative", width:22, height:22, flexShrink:0,
                      display:"inline-flex", alignItems:"center", justifyContent:"center" }}>
                      <span style={{ position:"absolute", width:20, height:20, borderRadius:"50%",
                        background:"#5F1156", opacity:0.15 }}/>
                      <span style={{ position:"absolute", width:10, height:10, borderRadius:"50%",
                        background:"#5F1156", border:"1.5px solid #fff" }}/>
                    </span>
                    <span style={{ fontSize:10, fontWeight:700, color:"#5F1156",
                      textTransform:"uppercase", letterSpacing:0.6 }}>Target Markets</span>
                    <CiteLink source={co.target_market_source} />
                  </div>
                  <div style={{ paddingLeft:19 }}>
                    {targetMarkets.length > 0 ? (
                      <div style={{ display:"flex", flexWrap:"wrap" }}>
                        {targetMarkets.map((m, i) => (
                          <span key={i} style={{ display:"inline-flex", alignItems:"center", gap:4,
                            fontSize:11, color:"#5F1156", background:"#EFDDE8",
                            border:"1px solid #5F115625", borderRadius:5,
                            padding:"3px 10px", margin:"2px 4px 2px 0", fontWeight:500 }}>
                            {getFlag(m)} {m}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span style={{ fontSize:11, color:"#D8DADC" }}>Not available</span>
                    )}
                  </div>
                </div>

                {/* ── 3. Shows Attended ── */}
                <div>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                    <span style={{ position:"relative", width:22, height:22, flexShrink:0,
                      display:"inline-flex", alignItems:"center", justifyContent:"center" }}>
                      <span style={{ position:"absolute", width:20, height:20, borderRadius:"50%",
                        background:"#E8431A", opacity:0.15 }}/>
                      <span style={{ position:"absolute", width:10, height:10, borderRadius:"50%",
                        background:"#E8431A", border:"1.5px solid #fff" }}/>
                    </span>
                    <span style={{ fontSize:10, fontWeight:700, color:"#16a34a",
                      textTransform:"uppercase", letterSpacing:0.6 }}>Shows Attended</span>
                    <CiteLink source={co.past_events_source} />
                  </div>
                  <div style={{ paddingLeft:19 }}>
                    {pastEvents.length > 0 ? (
                      <div style={{ border:"1px solid #f0f0f0", borderRadius:8, overflow:"hidden" }}>
                        {pastEvents.map((e, i) => {
                          const m    = e.match(/^(.*?)\s+(\d{4})$/);
                          const name = m ? m[1].trim() : e;
                          const year = m ? m[2] : null;
                          return (
                            <div key={i} style={{
                              display:"flex", alignItems:"center", justifyContent:"space-between",
                              padding:"8px 12px",
                              borderBottom: i < pastEvents.length - 1 ? "1px solid #F4F4F5" : "none",
                              background: i % 2 === 0 ? "#fafafa" : "#fff",
                            }}>
                              <span style={{ fontSize:11, color:"#3F3F46", flex:1,
                                minWidth:0, paddingRight:10, lineHeight:1.4 }}>
                                {name}
                              </span>
                              {year && (
                                <span style={{ fontSize:10, fontWeight:600, color:"#818084",
                                  background:"#F4F4F5", border:"1px solid #D8DADC",
                                  borderRadius:4, padding:"1px 8px", flexShrink:0 }}>
                                  {year}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <span style={{ fontSize:11, color:"#D8DADC" }}>Not available</span>
                    )}
                  </div>
                </div>

                {/* ── 4. Active Regions ── peer section, not nested under shows */}
                {eventRegions.length > 0 && (
                  <div>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                      <span style={{ position:"relative", width:22, height:22, flexShrink:0,
                        display:"inline-flex", alignItems:"center", justifyContent:"center" }}>
                        <span style={{ position:"absolute", width:20, height:20, borderRadius:"50%",
                          background:"#E8431A", opacity:0.15 }}/>
                        <span style={{ position:"absolute", width:10, height:10, borderRadius:"50%",
                          background:"#E8431A", border:"1.5px solid #fff" }}/>
                      </span>
                      <span style={{ fontSize:10, fontWeight:700, color:"#16a34a",
                        textTransform:"uppercase", letterSpacing:0.6 }}>Active Regions</span>
                      <CiteLink source={co.event_regions_source} />
                    </div>
                    <div style={{ paddingLeft:19, display:"flex", flexWrap:"wrap", gap:6 }}>
                      {eventRegions
                        .map(r => r.replace(/\s*\(.*?\)\s*/g, "").trim())
                        .filter(Boolean)
                        .map((r, i) => (
                          <span key={i} style={{
                            display:"inline-flex", alignItems:"center", gap:5,
                            fontSize:11, color:"#3F3F46", background:"#FAFAFA",
                            border:"1px solid #D8DADC", borderRadius:6,
                            padding:"4px 10px", fontWeight:500,
                          }}>
                            {getFlag(r)} {r}
                          </span>
                        ))}
                    </div>
                  </div>
                )}

              </div>

            </div>
          </SectionCard>

          {/* ── 5. Citation log — collapsible ─────────────────────────────── */}
          <CitationLog co={co} />

        </div>

        {/* ── Sticky Decision Bar — shown only in Lead Review context ── */}
        {leadReviewCtx && (
          <div style={{ flexShrink:0, background:"#fff", borderTop:"2px solid #D8DADC", boxShadow:"0 -4px 20px rgba(0,0,0,0.08)" }}>

            {/* Generated content strip — expands above the bar when pitch or email exists */}
            {(leadReviewCtx._pitch || leadReviewCtx._email) && (
              <div style={{ borderBottom:"1px solid #D8DADC", padding:"12px 28px 8px" }}>
                <div style={{ display:"flex", gap:16, marginBottom:8 }}>
                  {leadReviewCtx._pitch && (
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:5 }}>
                        <span style={{ fontSize:9, fontWeight:700, color:"#3F3F46", textTransform:"uppercase", letterSpacing:0.5 }}>Sales Script</span>
                        <button onClick={() => { navigator.clipboard?.writeText(leadReviewCtx._pitch).catch(()=>{}); showToast("Sales script copied!"); }}
                          style={{ fontSize:9, padding:"2px 7px", borderRadius:4, border:"1px solid #D8DADC", background:"#fff", color:"#818084", cursor:"pointer", fontFamily:F }}>Copy ↗</button>
                        <button onClick={() => setLeadReviewCtx(prev => ({...prev, _pitch:""}))}
                          style={{ fontSize:10, padding:"1px 6px", borderRadius:4, border:"none", background:"none", color:"#818084", cursor:"pointer", fontFamily:F, marginLeft:"auto" }}>✕</button>
                      </div>
                      <div style={{ fontSize:11, color:"#26262A", lineHeight:1.7, whiteSpace:"pre-wrap", background:"#fff", borderRadius:6, padding:"10px 14px", border:"1px solid #D8DADC", maxHeight:100, overflow:"auto" }}>{leadReviewCtx._pitch}</div>
                    </div>
                  )}
                  {leadReviewCtx._email && (
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:5 }}>
                        <span style={{ fontSize:9, fontWeight:700, color:"#3F3F46", textTransform:"uppercase", letterSpacing:0.5 }}>Prospect Email</span>
                        <button onClick={() => { navigator.clipboard?.writeText(leadReviewCtx._email).catch(()=>{}); showToast("Prospect email copied!"); }}
                          style={{ fontSize:9, padding:"2px 7px", borderRadius:4, border:"1px solid #D8DADC", background:"#fff", color:"#818084", cursor:"pointer", fontFamily:F }}>Copy ↗</button>
                        <button onClick={() => setLeadReviewCtx(prev => ({...prev, _email:""}))}
                          style={{ fontSize:10, padding:"1px 6px", borderRadius:4, border:"none", background:"none", color:"#818084", cursor:"pointer", fontFamily:F, marginLeft:"auto" }}>✕</button>
                      </div>
                      <div style={{ fontSize:11, color:"#26262A", lineHeight:1.7, whiteSpace:"pre-wrap", background:"#fff", borderRadius:6, padding:"10px 14px", border:"1px solid #D8DADC", maxHeight:100, overflow:"auto" }}>{leadReviewCtx._email}</div>
                    </div>
                  )}
                </div>
                <div style={{ fontSize:10, color:"#92400e", background:"#fef4e6", border:"1px solid #f9d9a8", borderRadius:6, padding:"5px 10px", display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                  <span style={{ fontSize:12 }}>⚠</span>
                  AI-generated content — always review before outreach.
                </div>
              </div>
            )}

            {/* Main decision row */}
            <div style={{ padding:"10px 28px", display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
              <span style={{ fontSize:9, fontWeight:700, color:"#AFABAB", textTransform:"uppercase", letterSpacing:0.6, flexShrink:0, marginRight:4 }}>Decision</span>
              {(() => {
                const currentReviewStatus =
                  (reviewCompanies[leadReviewCtx.asgnId] || []).find(c => c.id === leadReviewCtx.co.id)?.reviewStatus
                  || leadReviewCtx.co?.reviewStatus;
                const currentBucket = marketingBucketOf(currentReviewStatus);
                return leadReviewCtx.actions.map(({ s, l, c: ac }) => {
                  // Marketing actions are bucket ids (campaign/rejected/…); sales actions
                  // are status labels — so match each against the right shape.
                  const isActive = leadReviewCtx.isMH ? (currentBucket === s) : (currentReviewStatus === s);
                  const isMktPush = leadReviewCtx.isMH && s === "campaign";
                  const label     = isMktPush ? "Marketing Qualified Lead" : l;
                  return (
                    <button key={s} onClick={() => {
                      const SALES_PROMPT = ["Rejected","Mark Not Met","Needs Research","Refer to Another Event"];
                      if (!leadReviewCtx.isMH && SALES_PROMPT.includes(s)) {
                        sharedRequestDecision(leadReviewCtx.asgnId, leadReviewCtx.co.id, s, co.company_name, leadReviewCtx.asgnName);
                      } else if (leadReviewCtx.isMH && s === "rejected") {
                        sharedRequestDecision(leadReviewCtx.asgnId, leadReviewCtx.co.id, "Rejected", co.company_name, leadReviewCtx.asgnName);
                      } else {
                        leadReviewCtx.onDecide(s);
                      }
                    }}
                      style={{ padding:"6px 16px", borderRadius:20,
                        border:`1px solid ${isActive ? ac : ac+"80"}`,
                        background: isActive ? ac : `${ac}0d`,
                        color: isActive ? "#fff" : ac,
                        fontSize:11, fontWeight: isActive ? 700 : 500,
                        cursor: "pointer",
                        fontFamily:F, whiteSpace:"nowrap", transition:"all 0.1s" }}
                      onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background=ac+"22"; e.currentTarget.style.borderColor=ac+"aa"; } }}
                      onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background=ac+"0d"; e.currentTarget.style.borderColor=ac+"80"; } }}>
                      {label}
                    </button>
                  );
                });
              })()}
              <div style={{ flex:1, minWidth:160 }}>
                <textarea rows={1} placeholder="Add a note…" defaultValue={leadReviewCtx.co?.note || ""}
                  onFocus={e => e.currentTarget.rows = 3}
                  onBlur={e => {
                    e.currentTarget.rows = 1;
                    const v = e.target.value.trim();
                    if (v !== (leadReviewCtx.co?.note || "").trim()) {
                      const currentStatus =
                        (reviewCompanies[leadReviewCtx.asgnId] || []).find(c => c.id === leadReviewCtx.co.id)?.reviewStatus
                        || leadReviewCtx.co?.reviewStatus || "Pending";
                      updateReviewStatus(leadReviewCtx.asgnId, leadReviewCtx.co.id, currentStatus, {decisionNote: v}, true);
                    }
                  }}
                  style={{ width:"100%", padding:"6px 11px", borderRadius:7, border:"1px solid #D8DADC", fontSize:10.5, fontFamily:F, outline:"none", resize:"none", boxSizing:"border-box", color:"#3F3F46", background:"#FAFAFA", lineHeight:1.5, transition:"all 0.15s" }}/>
              </div>
              {!leadReviewCtx.isMH && (
              <div style={{ borderLeft:"1px solid #D8DADC", paddingLeft:10, flexShrink:0, position:"relative" }}>
                {aiMenuOpen && <div style={{ position:"fixed", inset:0, zIndex:99 }} onClick={() => setAiMenuOpen(false)}/>}
                <button onClick={() => setAiMenuOpen(o => !o)}
                  style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 12px", borderRadius:7, border:"1px solid #DFC4D4", background: aiMenuOpen ? "#EFDDE8" : "#F5EEF2", color:"#5F1156", fontSize:10.5, fontWeight:600, cursor:"pointer", fontFamily:F, whiteSpace:"nowrap", position:"relative", zIndex:100 }}>
                  <Sparkles size={11} />AI Assist <span style={{ fontSize:9, opacity:0.7 }}>{aiMenuOpen ? "▲" : "▼"}</span>
                </button>
                {aiMenuOpen && (
                  <div style={{ position:"absolute", bottom:"calc(100% + 6px)", right:0, background:"#fff", border:"1px solid #D8DADC", borderRadius:9, padding:4, boxShadow:"0 4px 16px rgba(0,0,0,0.1)", zIndex:200, minWidth:180 }}>
                    <button
                      onClick={() => {
                        const pitch = `${co.company_name} is a ${co.primary_event_category||co.company_type||"trade"} company from ${co.hq_country} exhibiting at ${leadReviewCtx.asgnName}.\n\nKey angle: ITE's ${leadReviewCtx.asgnEvent} offers direct access to qualified buyers that ${leadReviewCtx.asgnName} cannot match.\n\nOpening: "We noticed your presence at ${leadReviewCtx.asgnName} — we have strong buyer demand for ${co.primary_event_category||"your product category"} at ${leadReviewCtx.asgnEvent}."`;
                        setLeadReviewCtx(prev => ({...prev, _pitch: pitch}));
                        setAiMenuOpen(false);
                      }}
                      style={{ display:"flex", alignItems:"center", gap:7, width:"100%", padding:"8px 12px", borderRadius:6, border:"none", background:"transparent", color:"#5F1156", fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:F, textAlign:"left" }}
                      onMouseEnter={e => e.currentTarget.style.background="#F5EEF2"}
                      onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                      ✦ Sales Script
                    </button>
                    <button
                      onClick={() => {
                        const email = `Subject: ${leadReviewCtx.asgnEvent} — Exhibitor Opportunity\n\nDear ${co.company_name} Team,\n\nI noticed your exhibit at ${leadReviewCtx.asgnName} and wanted to reach out about a complementary opportunity.\n\nWe run ${leadReviewCtx.asgnEvent}, with strong buyer demand in your category.\n\nI'd welcome a short call at your convenience.\n\nBest regards,\n[Your Name]\n${leadReviewCtx.asgnEvent} — Exhibitor Sales`;
                        setLeadReviewCtx(prev => ({...prev, _email: email}));
                        setAiMenuOpen(false);
                      }}
                      style={{ display:"flex", alignItems:"center", gap:7, width:"100%", padding:"8px 12px", borderRadius:6, border:"none", background:"transparent", color:"#5F1156", fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:F, textAlign:"left" }}
                      onMouseEnter={e => e.currentTarget.style.background="#F5EEF2"}
                      onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                      ✉ Prospect Email
                    </button>
                  </div>
                )}
              </div>
              )}
            </div>

          </div>
        )}

      </div>
    </div>

    </ShowWebsiteContext.Provider>
  );
}
